import React from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaRocket, FaLightbulb, FaLaptopCode } from 'react-icons/fa';
import { SiJavascript, SiPython, SiReact, SiNodedotjs, SiDjango, SiNextdotjs, SiMongodb, SiPostgresql, SiDocker, SiKubernetes, SiGit, SiTailwindcss } from 'react-icons/si';
import LogoLoop from './ui/LogoLoop';
import BlurText from './ui/BlurText';

const Hero = () => {
  const techLogos = [
    { node: <SiReact className="text-[length:inherit] drop-shadow-lg" />, title: "React", href: "https://react.dev" },
    { node: <SiNextdotjs className="text-[length:inherit] drop-shadow-lg" />, title: "Next.js", href: "https://nextjs.org" },
    { node: <SiNodedotjs className="text-[length:inherit] drop-shadow-lg" />, title: "Node.js", href: "https://nodejs.org" },
    { node: <SiDjango className="text-[length:inherit] drop-shadow-lg" />, title: "Django", href: "https://www.djangoproject.com" },
    { node: <SiPython className="text-[length:inherit] drop-shadow-lg" />, title: "Python", href: "https://www.python.org" },
    { node: <SiJavascript className="text-[length:inherit] drop-shadow-lg" />, title: "JavaScript", href: "https://developer.mozilla.org/en-US/docs/Web/JavaScript" },
    { node: <SiTailwindcss className="text-[length:inherit] drop-shadow-lg" />, title: "Tailwind CSS", href: "https://tailwindcss.com" },
    { node: <SiMongodb className="text-[length:inherit] drop-shadow-lg" />, title: "MongoDB", href: "https://www.mongodb.com" },
    { node: <SiPostgresql className="text-[length:inherit] drop-shadow-lg" />, title: "PostgreSQL", href: "https://www.postgresql.org" },
    { node: <SiDocker className="text-[length:inherit] drop-shadow-lg" />, title: "Docker", href: "https://www.docker.com" },
    { node: <SiKubernetes className="text-[length:inherit] drop-shadow-lg" />, title: "Kubernetes", href: "https://kubernetes.io" },
    { node: <SiGit className="text-[length:inherit] drop-shadow-lg" />, title: "Git", href: "https://git-scm.com" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section id="technologies" className="py-12 sm:py-16 md:py-20 relative overflow-hidden bg-gradient-to-b from-darker to-dark">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Tech Stack Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-12 md:mb-16"
        >
          <div className="inline-flex items-center space-x-2 glass-strong px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-primary/30">
            <FaCode className="text-primary animate-pulse text-sm sm:text-base" />
            <span className="text-xs sm:text-sm font-mono text-light uppercase tracking-wider">Tech Stack</span>
          </div>

          <BlurText
            text="Technologies We Master"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-center"
            childClassName="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-emerald"
            delay={150}
            animateBy="words"
            direction="top"
          />

          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Leveraging cutting-edge technologies to build powerful, scalable, and modern applications
          </p>
        </motion.div>

        {/* LogoLoop */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-8 sm:mb-12 md:mb-16"
        >
          <div className="glass-strong rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-primary/20 overflow-hidden">
            <LogoLoop logos={techLogos} speed={60} />
          </div>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
        >
          <motion.div
            variants={itemVariants}
            className="group glass-strong rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-primary/20 hover:border-primary/50 transition-all duration-500 hover:-translate-y-2 sm:hover:-translate-y-3 hover:shadow-2xl hover:shadow-primary/30 text-center overflow-hidden relative"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 mb-4 sm:mb-6 group-hover:scale-110 transition-all duration-500 border border-primary/30">
                <FaLaptopCode className="text-3xl sm:text-4xl text-primary group-hover:text-accent transition-colors duration-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-light mb-3 sm:mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent transition-all duration-500">
                Modern Stack
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                React, Next.js, Node.js, Django - using the latest frameworks for optimal performance
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="group glass-strong rounded-3xl p-8 border border-accent/20 hover:border-accent/50 transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-accent/30 text-center overflow-hidden relative"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-accent/20 to-emerald/20 mb-6 group-hover:scale-110 transition-all duration-500 border border-accent/30">
                <FaRocket className="text-4xl text-accent group-hover:text-emerald transition-colors duration-500" />
              </div>
              <h3 className="text-2xl font-bold text-light mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-accent group-hover:to-emerald transition-all duration-500">
                Production Ready
              </h3>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                Docker, Kubernetes, Git - industry-standard tools for deployment and version control
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="group glass-strong rounded-3xl p-8 border border-emerald/20 hover:border-emerald/50 transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-emerald/30 text-center overflow-hidden relative"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald/20 to-primary/20 mb-6 group-hover:scale-110 transition-all duration-500 border border-emerald/30">
                <FaLightbulb className="text-4xl text-emerald group-hover:text-primary transition-colors duration-500" />
              </div>
              <h3 className="text-2xl font-bold text-light mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald group-hover:to-primary transition-all duration-500">
                Full Stack Power
              </h3>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                MongoDB, PostgreSQL, Tailwind CSS - complete solutions from database to UI design
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
